import React, { useState, useEffect, useRef } from 'react'
import { Fancybox as NativeFancybox } from '@fancyapps/ui'
import { Helmet } from 'react-helmet'
import { useParams } from 'react-router-dom'
import {
  Calendar,
  Globe,
  Users,
  Play,
  Menu,
  X,
  Minus,
  Plus,
} from 'lucide-react'
import Imgix from 'react-imgix'
import 'lazysizes'
import 'lazysizes/plugins/parent-fit/ls.parent-fit'
import 'lazysizes/plugins/attrchange/ls.attrchange'
import { ModalProvider, useUserService } from '@abroad/components'
import ScheduleCallModal from '../components/ScheduleCallModal/ScheduleCallModal'
import ScheduleCallButton from '../components/ScheduleCallButton/ScheduleCallButton'
import FacultyModal from '../components/assessmentResult/FacultyModal'
import LearnMoreSection from '../components/prosperity/LearnMoreSection'
import API from '../utils/API'
import {
  getFormattedDateRange,
  getProgramDatesRange,
  formatProgramDateEntry,
  sortProgramDatesList,
} from '../utils/date'
import { formatUtcRsvpForDisplay } from '../utils/rsvpDateTime'

// --- Icon Components (using Bootstrap Icons classes) ---

const IMGIX_LQIP_PARAMS = 'fill=blur&blur=300&px=16&auto=format&q=10'

// react-imgix fixed-size mode otherwise emits dpr=2&q=50 (etc.), which softens
// retina images. Keep quality stable across DPR; avoid auto=compress.
const IMGIX_SHARP_PARAMS = {
  auto: 'format',
  q: 100,
  cs: 'srgb',
  lossless: 1,
}

const MOBILE_BREAKPOINT = 768
const HERO_DESKTOP_WIDTH = 3200
const HERO_MOBILE_WIDTH = 1600

// Journey cards are ~half-width × 400–500px CSS and scale up to ~1.15× (parallax).
const JOURNEY_CARD_WIDTH = 1200
const JOURNEY_CARD_HEIGHT = 750

const getHeroImageDimensions = (isMobile) => {
  const width = isMobile ? HERO_MOBILE_WIDTH : HERO_DESKTOP_WIDTH
  const height = Math.round(
    (width * (isMobile ? 186 : 465)) / (isMobile ? 85 : 1024),
  )
  return { width, height }
}

// --- Utility Functions ---
const formatNumberWithCommas = (num) => {
  const numericValue = Number(num)
  return isNaN(numericValue) ? '' : numericValue.toLocaleString('en-US')
}

// --- Components ---

// FancyboxVideo Component (adapted)
const FancyboxVideoWrapper = (props) => {
  const containerRef = useRef(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const delegate = '[data-fancybox]'
    const options = {
      Toolbar: {
        display: [
          { id: 'counter', position: 'left' },
          'zoom',
          'slideshow',
          'fullscreen',
          'download',
          'close',
        ],
      },
      Youtube: {
        autoplay: 1,
        controls: 1,
        showinfo: 0,
        rel: 0,
      },
    }

    NativeFancybox.bind(container, delegate, options)

    return () => {
      NativeFancybox.destroy()
    }
  }, [])

  return (
    <div className={props.playIcon} ref={containerRef}>
      {props.children}
    </div>
  )
}

function VideoModal({ videoId, onClose, show }) {
  if (!show) return null
  return (
    <div
      className='modal fade show d-block'
      tabIndex='-1'
      style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}>
      <div className='modal-dialog modal-dialog-centered modal-xl'>
        <div className='modal-content bg-transparent border-0'>
          <div
            className='modal-header border-0 position-absolute top-0 end-0'
            style={{ zIndex: 2 }}>
            <button
              type='button'
              className='btn text-white'
              onClick={onClose}
              aria-label='Close'>
              <X />
            </button>
          </div>
          <div className='modal-body p-0'>
            <div className='ratio ratio-16x9'>
              <iframe
                src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&cc_load_policy=0&cc_lang_pref=en&hl=en`}
                title='Video Player'
                frameBorder='0'
                allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                allowFullScreen></iframe>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function FAQ({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false)
  const uniqueId = question.replace(/\s+/g, '-').toLowerCase() // Keep uniqueId for ARIA and collapse functionality

  return (
    <div className='accordion-item faq-item-wrapper'>
      {' '}
      {/* Bootstrap: accordion-item */}
      <h2 className='accordion-header' id={`faq-heading-${uniqueId}`}>
        {' '}
        {/* Bootstrap: accordion-header */}
        <button
          className={`accordion-button px-0 py-4 font-heading fs-5 fw-medium d-flex justify-content-between align-items-center`}
          type='button'
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          aria-controls={`faq-collapse-${uniqueId}`}>
          <span className='me-auto font-heading sub-title-2 text-rich-navy'>
            {question}
          </span>{' '}
          {/* Question text taking available space */}
          {isOpen ? (
            <Minus
              className='flex-shrink-0 w-6 h-6 text-gold-light'
              style={{
                width: '24px',
                height: '24px',
                color: 'var(--bs-gold-light)',
              }}
            />
          ) : (
            <Plus
              className='flex-shrink-0 w-6 h-6 text-gold-light'
              style={{
                width: '24px',
                height: '24px',
                color: 'var(--bs-gold-light)',
              }}
            />
          )}
        </button>
      </h2>
      <div
        id={`faq-collapse-${uniqueId}`}
        className={`accordion-collapse collapse prosperity-accordion  ${
          isOpen ? 'show' : ''
        }`} // Bootstrap: accordion-collapse, collapse, show
        aria-labelledby={`faq-heading-${uniqueId}`}
        data-bs-parent='#faqAccordionParent'>
        {' '}
        {/* Optional: if you want only one item open at a time within a parent */}
        <div className='accordion-body px-0 py-0 text-slate-gray mb-24px font-paragraph small-paragraph fw-medium lh-relaxed'>
          {answer.split('\n\n').map((paragraph, index) => (
            <p key={index} className='mb-3'>
              {paragraph}
            </p>
          ))}
        </div>
      </div>
    </div>
  )
}

function TestimonialStrip({
  videos = [],
  title = 'Reflections from Bhutan',
  testimonialInfo = [],
}) {
  const stripRef = useRef(null)

  useEffect(() => {
    const handleScroll = () => {
      if (!stripRef.current) return

      const images = stripRef.current.querySelectorAll('.testimonial-image')
      images.forEach((image) => {
        const container = image.closest('.testimonial-container')
        if (!container) return

        const rect = container.getBoundingClientRect()
        const viewportHeight = window.innerHeight

        if (rect.top < viewportHeight && rect.bottom > 0) {
          const scrollProgress =
            (viewportHeight - rect.top) / (viewportHeight + rect.height)
          const scale = 1 + 0.1 * Math.min(Math.max(scrollProgress, 0), 1)
          image.style.transform = `scale(${scale})`
        }
      })
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll() // Initial check

    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  if (!videos || videos.length === 0) return null

  const validVideos = videos.filter((videoId) => videoId)
  const validTestimonials = testimonialInfo.slice(0, validVideos.length)

  if (validVideos.length === 0) return null
  return (
    <div className='testimonial-strip' ref={stripRef}>
      <h2 className='display-5 font-heading sub-title mb-5 text-rich-navy'>
        {title}
      </h2>
      <div className='row g-4'>
        {validVideos.map((video, index) => (
          <div key={index} className='col-md-4 d-flex flex-column'>
            <FancyboxVideoWrapper
              options={{
                groupAttr: `group-${title.replace(/\s+/g, '-').toLowerCase()}`,
              }}>
              <a
                href={`https://www.youtube.com/watch?v=${video}&cc_load_policy=0&cc_lang_pref=en&hl=en`}
                data-fancybox={`group-${title
                  .replace(/\s+/g, '-')
                  .toLowerCase()}`}
                data-caption={validTestimonials[index] || ''}
                className='d-block'>
                <div className='testimonial-item aspect-ratio-1x1 position-relative border-radius-12 overflow-hidden shadow-lg mb-3'>
                  <div className='testimonial-container position-relative w-100 h-100'>
                    <img
                      src={`https://img.youtube.com/vi/${video}/maxresdefault.jpg`}
                      alt={
                        validTestimonials[index] || `Testimonial ${index + 1}`
                      }
                      className='testimonial-image w-100 h-100 object-cover'
                      style={{
                        aspectRatio: '16/9',
                        transition:
                          'transform 0.6s cubic-bezier(0.33, 1, 0.68, 1)',
                        transformOrigin: 'center center',
                        willChange: 'transform',
                      }}
                    />
                    <div className='position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center'>
                      <div className='play-button-wrapper'>
                        <Play className='w-8 h-8 text-black fill-current' />
                      </div>
                    </div>
                  </div>
                </div>
              </a>
            </FancyboxVideoWrapper>
            <p className='mb-0 text-slate-gray small fw-semibold font-paragraph small-paragraph lh-relaxed text-start'>
              {validTestimonials[index] || ''}
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}

function JourneyCardMedia({ imageUrl, title }) {
  const [imageFailed, setImageFailed] = useState(false)
  const trimmedUrl = typeof imageUrl === 'string' ? imageUrl.trim() : ''
  const hasUrl = Boolean(trimmedUrl)

  useEffect(() => {
    setImageFailed(false)
  }, [trimmedUrl])

  const showPlaceholder = !hasUrl || imageFailed

  if (showPlaceholder) {
    return (
      <div
        className='parallax-container parallax-height journey-card-media-placeholder d-flex align-items-center justify-content-center'
        role='img'
        aria-label={title}>
        <p className='text-slate-gray small lh-relaxed font-paragraph small-paragraph'>
          {title}
        </p>
      </div>
    )
  }

  return (
    <div className='parallax-container parallax-height'>
      <Imgix
        className='lazyload w-100 h-100 object-cover parallax-image'
        src={trimmedUrl}
        width={JOURNEY_CARD_WIDTH}
        height={JOURNEY_CARD_HEIGHT}
        disableQualityByDPR
        imgixParams={{
          fit: 'crop',
          ...IMGIX_SHARP_PARAMS,
        }}
        attributeConfig={{
          src: 'data-src',
          srcSet: 'data-srcset',
          sizes: 'data-sizes',
        }}
        htmlAttributes={{
          src: `${trimmedUrl}?w=${JOURNEY_CARD_WIDTH}&h=${JOURNEY_CARD_HEIGHT}&fit=crop&${IMGIX_LQIP_PARAMS}`,
          alt: title,
          onError: () => setImageFailed(true),
          style: {
            objectFit: 'cover',
            position: 'absolute',
            top: 0,
            left: 0,
          },
        }}
      />
    </div>
  )
}

function JourneyStep({ step, index }) {
  if (step.testimonials) {
    return <TestimonialStrip videos={step.testimonials} />
  }

  const renderJourneyCard = (item, itemMonth, itemLabel, key) => (
    <div
      key={key}
      className='bg-white mb-64px border-radius-custom overflow-hidden d-flex flex-column flex-md-row hover-lift prosperity-card'>
      <div className='col-md-6 p-4 p-lg-5 d-flex flex-column justify-content-center order-2 order-md-1'>
        <div className='text-gold-light fw-semibold small font-paragraph small-paragraph-2 mb-3'>
          {`${itemMonth}  ${itemLabel}`}
        </div>
        <h3 className='mb-3 fw-medium text-rich-navy prosperity-card-line-height font-heading sub-title'>
          {item.title}
        </h3>
        <div
          className='text-slate-gray mb-0 lh-relaxed pre font-paragraph small-paragraph'
          dangerouslySetInnerHTML={{ __html: item.description }}
        />
      </div>
      <div className='col-md-6 position-relative order-1 order-md-2 overflow-hidden'>
        <JourneyCardMedia imageUrl={item.image} title={item.title} />
      </div>
    </div>
  )

  return (
    <div className='journey-step-item' data-index={index}>
      {step.sections ? (
        <div className='vstack'>
          {step.sections.map((section, sectionIndex) =>
            renderJourneyCard(section, step.month, section.label, sectionIndex),
          )}
        </div>
      ) : (
        renderJourneyCard(step, step.month, step.label, 0)
      )}
    </div>
  )
}

const Prosperity = () => {
  const [currentImage, setCurrentImage] = useState(0)
  const [showStickyHeader, setShowStickyHeader] = useState(false)
  const [showPricingInHeader, setShowPricingInHeader] = useState(false)
  const [isPastInclusions, setIsPastInclusions] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [showMobileApplyButton, setShowMobileApplyButton] = useState(false)
  const [isMobileHero, setIsMobileHero] = useState(
    typeof window !== 'undefined'
      ? window.innerWidth < MOBILE_BREAKPOINT
      : false,
  )
  const [modalVideoId, setModalVideoId] = useState(null)
  const [questData, setQuestData] = useState(null)
  const [facultyModalMember, setFacultyModalMember] = useState(null)

  const { questId } = useParams()
  const { user } = useUserService()
  // Build carousel images (header images) from API if provided
  const displayCarouselImages = React.useMemo(() => {
    if (questData?.headerImage?.length) {
      return questData.headerImage.map((img) => ({
        url: `${process.env.REACT_APP_IMG_SOURCE}/quest/${img.fileName}`,
        caption: img.altText || '',
      }))
    }
    return []
  }, [questData, questId])

  const heroAspectRatio = isMobileHero ? '85:186' : '1024:465'
  const heroImageDimensions = React.useMemo(
    () => getHeroImageDimensions(isMobileHero),
    [isMobileHero],
  )

  const titleRef = useRef(null)
  const inclusionsRef = useRef(null)
  const pricingBoxRef = useRef(null)

  useEffect(() => {
    const fetchQuest = async () => {
      const { data } = await API.quests.getProspetityQuest(questId)
      setQuestData(data)
    }
    fetchQuest()
  }, [])

  useEffect(() => {
    const handleResize = () => {
      setIsMobileHero(window.innerWidth < MOBILE_BREAKPOINT)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Bidirectional sticky booking card via native position:sticky + dynamic top.
  // Scrolls with the page until it sticks to the top (up) or bottom (down).
  useEffect(() => {
    const sidebar = pricingBoxRef.current
    if (!sidebar || !questData) return

    const TOP_GAP = 100
    const BOTTOM_GAP = 24
    const MOBILE_MAX = 991

    let currentScrollY = window.scrollY
    let lastComputedTop = TOP_GAP
    let rafId = null

    const clearStyles = () => {
      sidebar.style.removeProperty('position')
      sidebar.style.removeProperty('height')
      sidebar.style.removeProperty('top')
      sidebar.classList.remove('is-sticky-synced')
    }

    const enableDesktopSticky = () => {
      if (window.innerWidth <= MOBILE_MAX) {
        clearStyles()
        return false
      }
      sidebar.classList.add('is-sticky-synced')
      sidebar.style.position = 'sticky'
      sidebar.style.height = 'fit-content'
      return true
    }

    const positionSidebar = () => {
      rafId = null

      if (!enableDesktopSticky()) {
        currentScrollY = window.scrollY
        lastComputedTop = TOP_GAP
        return
      }

      const viewportHeight = window.innerHeight
      const sidebarHeight = sidebar.offsetHeight
      const newScrollY = window.scrollY
      const scrollDelta = currentScrollY - newScrollY
      const bottomLimit = viewportHeight - sidebarHeight - BOTTOM_GAP

      if (sidebarHeight + TOP_GAP + BOTTOM_GAP <= viewportHeight) {
        lastComputedTop = TOP_GAP
      } else {
        let targetTop = lastComputedTop + scrollDelta
        if (newScrollY < currentScrollY) {
          targetTop = Math.min(TOP_GAP, targetTop)
        } else if (newScrollY > currentScrollY) {
          targetTop = Math.max(bottomLimit, targetTop)
        } else {
          targetTop = Math.min(TOP_GAP, Math.max(bottomLimit, targetTop))
        }
        lastComputedTop = targetTop
      }

      sidebar.style.top = `${lastComputedTop}px`
      currentScrollY = newScrollY
    }

    const onScroll = () => {
      if (rafId == null) {
        rafId = window.requestAnimationFrame(positionSidebar)
      }
    }

    const onResize = () => {
      enableDesktopSticky()
      positionSidebar()
    }

    enableDesktopSticky()
    positionSidebar()

    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onResize)

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            const viewportHeight = window.innerHeight
            const sidebarHeight = sidebar.offsetHeight
            if (sidebarHeight + TOP_GAP + BOTTOM_GAP <= viewportHeight) {
              lastComputedTop = TOP_GAP
            } else {
              const bottomLimit = viewportHeight - sidebarHeight - BOTTOM_GAP
              lastComputedTop = Math.min(
                TOP_GAP,
                Math.max(bottomLimit, lastComputedTop),
              )
            }
            if (window.innerWidth > MOBILE_MAX) {
              sidebar.style.top = `${lastComputedTop}px`
            }
          })
        : null

    if (resizeObserver) resizeObserver.observe(sidebar)

    return () => {
      if (rafId != null) window.cancelAnimationFrame(rafId)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onResize)
      if (resizeObserver) resizeObserver.disconnect()
      clearStyles()
    }
  }, [questData])

  useEffect(() => {
    if (!questData?.investments) return
    console.log('[Prosperity] singleSupplement', {
      singleSupplement: questData.investments.singleSupplement,
      standardRate: questData.investments.standardRate,
    })
  }, [questData])

  useEffect(() => {
    const styleElement = document.createElement('style')
    styleElement.innerHTML = `
      @keyframes marquee-scroll {
        0% { transform: translateX(0); }
        100% { transform: translateX(-100%); }
      }

      .accordion-button::after {
        background-image: none !important;
        margin-left:0 !important;
      }
    `
    document.head.appendChild(styleElement)
    return () => {
      document.head.removeChild(styleElement)
    }
  }, [])

  useEffect(() => {
    if (!displayCarouselImages.length) return
    const timer = setInterval(() => {
      setCurrentImage((current) => (current + 1) % displayCarouselImages.length)
    }, 6000)
    return () => clearInterval(timer)
  }, [displayCarouselImages])

  useEffect(() => {
    const handleScroll = () => {
      if (titleRef.current) {
        setShowStickyHeader(titleRef.current.getBoundingClientRect().bottom < 0)
      }
      if (inclusionsRef.current) {
        const inclusionsTop = inclusionsRef.current.getBoundingClientRect().top
        setIsPastInclusions(inclusionsTop < 0)
      }
      if (pricingBoxRef.current) {
        const rect = pricingBoxRef.current.getBoundingClientRect()
        const isBoxOffScreen = rect.bottom <= 0
        setShowPricingInHeader(isPastInclusions && isBoxOffScreen)
        setShowMobileApplyButton(isBoxOffScreen)
      }
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isPastInclusions])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
          }
        })
      },
      { threshold: 0.1 },
    )

    document
      .querySelectorAll(
        ' .faculty-member-item, .faq-item-wrapper, .included-item, .testimonial-item',
      )
      .forEach((item) => observer.observe(item))

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      document.querySelectorAll('.parallax-image').forEach((imageEl) => {
        const container = imageEl.closest('.parallax-container')
        if (!container) return
        const rect = container.getBoundingClientRect()
        const isVisible = rect.top < window.innerHeight && rect.bottom > 0

        if (isVisible) {
          const viewportPosition =
            (window.innerHeight - rect.top) / (window.innerHeight + rect.height)
          const scale = 1 + 0.15 * Math.min(Math.max(viewportPosition, 0), 1)
          imageEl.style.transform = `scale(${scale})`
        } else {
          imageEl.style.transform = 'scale(1)'
        }
      })
    }
    window.addEventListener('scroll', handleScroll)
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId)
    if (element) {
      const stickyHeaderOffset = 120 // matches .scroll-target scroll-margin-top
      const elementTop =
        element.getBoundingClientRect().top + window.pageYOffset
      const titleBottom = titleRef.current
        ? titleRef.current.getBoundingClientRect().bottom + window.pageYOffset
        : 0
      // Offset when the destination sits below the hero title, where the sticky
      // header will be visible once scroll finishes (not current scroll state).
      const headerOffset = elementTop > titleBottom ? stickyHeaderOffset : 0
      window.scrollTo({
        top: elementTop - headerOffset,
        behavior: 'smooth',
      })
    }
    if (isMenuOpen) setIsMenuOpen(false)
  }

  const closeModal = () => setModalVideoId(null)

  // Dynamically build Journey steps from questData. Fallback to default static data if API hasn't returned anything yet.
  const displayJourneySteps = React.useMemo(() => {
    if (questData?.journeySections?.length) {
      return questData.journeySections.map((section) => {
        // Expecting title in the format "Month X - Label". Safely split into parts.
        const [monthPart, ...labelParts] = (section.title || '').split('-')
        const month = monthPart?.trim() || ''
        const label = labelParts.join('-').trim()

        return {
          month,
          label,
          title: section.description || '',
          description: section.body || '',
          image: section.imageFileName
            ? `${process.env.REACT_APP_IMG_SOURCE}/quest/${section.imageFileName}`
            : undefined,
        }
      })
    }
    // If API hasn't returned anything, render nothing
    return []
  }, [questData, questId])

  // Dynamically build Faculty & Coaches list from questData. If API hasn't returned faculties yet, fall back to static list.
  const displayFacultyMembers = React.useMemo(() => {
    if (!questData?.faculties?.length) return null

    const visibleFaculties = questData.faculties.filter(
      (faculty) => faculty.isVisible,
    )

    if (!visibleFaculties.length) return null

    return visibleFaculties.map((member) => ({
      name: member.name,
      title: member.title || '',
      expertise: member.expertise || '',
      description: member.description || '',
      image: member.imageFileName
        ? `${process.env.REACT_APP_IMG_SOURCE}/quest/${member.imageFileName}`
        : undefined,
    }))
  }, [questData, questId])

  const informationSession = React.useMemo(() => {
    const rsvp = questData?.learnMore?.rsvp
    if (!rsvp?.date || !rsvp?.time || (!rsvp?.link && !rsvp?.recordingLink)) {
      return null
    }

    const formatted = formatUtcRsvpForDisplay(rsvp.date, rsvp.time)
    if (!formatted) return null

    return {
      date: formatted.date,
      time: formatted.time,
      rsvpLink: rsvp.link,
      recordingLink: rsvp.recordingLink,
    }
  }, [questData])

  // Dynamically build FAQs list from questData; fallback to static faqs.
  const displayFaqs = React.useMemo(() => {
    if (questData?.faqs?.length) {
      return questData.faqs.map((faq) => ({
        question: faq.question,
        answer: faq.answer,
      }))
    }
    return []
  }, [questData])

  // Helper function to check if there's content before journey section
  const hasContentBeforeJourney = () => {
    return (
      questData?.mainVideo?.youtubeLink ||
      (questData?.testimonialVideos?.firstRow &&
        Array.isArray(questData?.testimonialVideos?.firstRow.videos) &&
        questData?.testimonialVideos?.firstRow.videos.length > 0)
    )
  }

  // (hook will be declared inside component)
  // --- NOTE ---
  // Static demo data removed...

  return (
    <>
      {(questData?.isArchived || questData?.isVisible === false) && (
        <Helmet>
          <meta name='robots' content='noindex, nofollow' />
        </Helmet>
      )}
      <ModalProvider>
        <div className='bg-background prosperity-page'>
          <header
            className={`fixed-top header-shadow-md bg-white prosperity-header ${
              showStickyHeader
                ? 'sticky-header-visible'
                : 'sticky-header-hidden'
            }`}>
            <div className='container px-3 py-3 d-flex align-items-center justify-content-between'>
              <a href='https://abroad.io' className='navbar-brand'>
                <img
                  src='https://images.squarespace-cdn.com/content/v1/63db562b53fc5e6e59ef802c/98522d73-daa5-49ab-bc3c-0468ca75ee95/Group+1989.png'
                  alt='Abroad Logo'
                  style={{ height: '48px', width: '80px' }}
                />
              </a>
              <nav className='d-none d-lg-flex align-items-center prosperity-nav-desktop'>
                <button
                  onClick={() => scrollToSection('program')}
                  className='btn btn-link p-0 text-decoration-none text-rich-navy font-paragraph small-paragraph-3 prosperity-nav-link'>
                  Program
                </button>
                <button
                  onClick={() => scrollToSection('journey')}
                  className='btn btn-link p-0 ml-32px text-decoration-none text-rich-navy font-paragraph small-paragraph-3 prosperity-nav-link'>
                  Journey
                </button>
                <button
                  onClick={() => scrollToSection('faculty')}
                  className='btn btn-link p-0 ml-32px text-decoration-none text-rich-navy font-paragraph small-paragraph-3 prosperity-nav-link'>
                  Faculty
                </button>
                <button
                  onClick={() => scrollToSection('faq')}
                  className='btn btn-link p-0 ml-32px text-decoration-none text-rich-navy font-paragraph small-paragraph-3 prosperity-nav-link'>
                  FAQ
                </button>
                <button
                  onClick={() => scrollToSection('learn-more')}
                  className='btn btn-link p-0 ml-32px text-decoration-none text-rich-navy font-paragraph small-paragraph-3 prosperity-nav-link'>
                  Learn More
                </button>
              </nav>
              <button
                className='d-lg-none btn btn-link text-rich-navy p-0'
                onClick={() => setIsMenuOpen(true)}
                aria-label='Open menu'>
                <Menu />
              </button>

              <div
                className={`${
                  showPricingInHeader
                    ? 'd-lg-flex d-md-none d-none align-items-center prosperity-header-apply opacity-100 fade-up duration-500 transition-all'
                    : 'd-none d-lg-flex d-md-none align-items-center visibility-hidden fade-down duration-500 transition-all'
                }`}>
                <div className='text-end me-4'>
                  <p className='text-rich-navy fw-semibold mb-0 font-paragraph small-paragraph-3'>
                    $
                    {questData?.investments?.standardRate
                      ? formatNumberWithCommas(
                          questData?.investments?.standardRate,
                        )
                      : 0}
                  </p>
                  <p className='text-secondary font-paragraph small-paragraph-4 mb-0'>
                    {(() => {
                      const firstDate = questData?.programDatesList?.[0]
                      return getFormattedDateRange(
                        firstDate?.startDate,
                        firstDate?.endDate,
                      )
                    })()}
                  </p>
                </div>
                <ScheduleCallButton className='btn btn-gold text-white text-uppercase header-button-spacing prosperity-button-cta font-paragraph small-paragraph-bold'>
                  {questData?.inquiryButtonName || 'Apply Now'}{' '}
                </ScheduleCallButton>
              </div>
            </div>
          </header>

          <div>
            <div
              className={`d-lg-none fixed-bottom bg-white shadow-top  prosperity-mobile-cta ${
                showMobileApplyButton
                  ? 'fade-up duration-500 transition-all'
                  : 'mobs'
              }`}>
              {showMobileApplyButton ? (
                <div className='container px-4 py-3 d-flex align-items-center justify-content-between'>
                  <div>
                    <p className='text-rich-navy fw-semibold small mb-0 font-14px'>
                      $
                      {questData?.investments?.standardRate
                        ? formatNumberWithCommas(
                            questData?.investments?.standardRate,
                          )
                        : 0}{' '}
                    </p>
                    <p className='text-secondary extra-small mb-0 font-12px'>
                      {(() => {
                        const firstDate = questData?.programDatesList?.[0]
                        return getFormattedDateRange(
                          firstDate?.startDate,
                          firstDate?.endDate,
                        )
                      })()}
                    </p>
                  </div>
                  <ScheduleCallButton className='btn btn-gold text-white text-uppercase prosperity-button-cta small'>
                    {questData?.inquiryButtonName || 'Apply Now'}{' '}
                  </ScheduleCallButton>
                </div>
              ) : (
                <div></div>
              )}
            </div>
          </div>

          <section className='hero-section position-relative'>
            <div
              id='heroImageCarousel'
              className='prosperity-carousel position-relative overflow-hidden'>
              <div className='carousel-inner position-relative h-100'>
                {displayCarouselImages.map((image, index) => (
                  <div
                    key={index}
                    className={`carousel-slide position-absolute w-100 h-100 ${
                      index === currentImage ? 'active' : ''
                    } parallax-container`}>
                    <Imgix
                      className={`${
                        index === 0 ? '' : 'lazyload'
                      } d-block w-100 h-100 hero-carousel-image parallax-image`}
                      src={image.url}
                      width={heroImageDimensions.width}
                      height={heroImageDimensions.height}
                      disableSrcSet
                      disableQualityByDPR
                      imgixParams={{
                        ar: heroAspectRatio,
                        fit: 'crop',
                        ...IMGIX_SHARP_PARAMS,
                      }}
                      attributeConfig={
                        index === 0
                          ? undefined
                          : {
                              src: 'data-src',
                              srcSet: 'data-srcset',
                              sizes: 'data-sizes',
                            }
                      }
                      htmlAttributes={{
                        alt: image.caption,
                        decoding: 'async',
                        ...(index === 0 && {
                          fetchpriority: 'high',
                          loading: 'eager',
                        }),
                        ...(index !== 0 && {
                          src: `${image.url}?ar=${heroAspectRatio}&fit=crop&${IMGIX_LQIP_PARAMS}`,
                        }),
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
            <nav className='position-absolute top-0 start-0 end-0 pt-3 z-1'>
              <div className='container px-3 d-flex justify-content-center justify-content-md-start'>
                <a
                  href={`${user ? '/experiences' : 'https://www.abroad.io/'}`}
                  className='navbar-brand'>
                  <img
                    src='https://images.squarespace-cdn.com/content/v1/63db562b53fc5e6e59ef802c/98522d73-daa5-49ab-bc3c-0468ca75ee95/Group+1989.png'
                    alt='Abroad Logo'
                    style={{ height: '59px', width: '98px' }}
                  />
                </a>
              </div>
            </nav>
          </section>

          <main className='content-fade-in'>
            <div className='container px-3 py-5'>
              <div className='row'>
                <div className='col-lg-8 pe-lg-4'>
                  <h1
                    ref={titleRef}
                    className='display-2 font-heading main-title mb-4 text-rich-navy'>
                    {questData?.title}
                  </h1>
                  <p
                    className='font-16 text-gray-700 fw-medium mb-5 max-w-text-lg font-paragraph main-paragraph'
                    style={{ whiteSpace: 'pre-wrap' }}>
                    {questData?.description}
                  </p>
                  {(questData?.duration ||
                    questData?.groupSize ||
                    questData?.location) && (
                    <div className='border-top border-bottom py-40px my-5'>
                      <div className='row g-4 text-md-start'>
                        {questData?.duration && (
                          <div className='col-md-4'>
                            <div className='d-flex align-items-center justify-content-md-start mb-12px'>
                              <Calendar className='w-6 h-6 mr-12px text-gold-light' />
                              <div className='prosperity-icon-heading font-montserrat extra-small'>
                                Duration
                              </div>
                            </div>
                            <p className='text-slate-gray fw-medium mb-0'>
                              {questData?.duration}{' '}
                            </p>
                          </div>
                        )}
                        {questData?.groupSize && (
                          <div className='col-md-4'>
                            <div className='d-flex align-items-center justify-content-md-start mb-12px'>
                              <Users className='w-6 h-6 mr-12px text-gold-light' />
                              <div className='prosperity-icon-heading font-montserrat extra-small'>
                                Group Size
                              </div>
                            </div>
                            <p className='text-slate-gray fw-medium mb-0'>
                              {questData?.groupSize}
                            </p>
                          </div>
                        )}
                        {questData?.location && (
                          <div className='col-md-4'>
                            <div className='d-flex align-items-center justify-content-md-start mb-12px'>
                              <Globe className='w-6 h-6 mr-12px text-gold-light' />
                              <div className='prosperity-icon-heading font-montserrat extra-small'>
                                Location
                              </div>
                            </div>
                            <p className='text-slate-gray fw-medium mb-0'>
                              {questData?.location}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {questData?.partnerships?.length > 0 && (
                    <section className='mb-64px'>
                      <h2 className='display-5 font-heading sub-title mb-5 text-rich-navy fw-medium'>
                        In Partnership With
                      </h2>
                      <div className='row row-cols-1 row-cols-sm-3 g-4'>
                        {questData?.partnerships?.map((partnership, index) => (
                          <div key={index} className='col'>
                            <div className='hover-lift p-32px rounded-3 bg-white prosperity-card'>
                              <div className='partner-logo d-flex align-items-center justify-content-center'>
                                <Imgix
                                  className='lazyload partner-logo-img'
                                  src={`${process.env.REACT_APP_IMG_SOURCE}/quest/${partnership.logoFileName}`}
                                  width={180}
                                  height={90}
                                  disableQualityByDPR
                                  imgixParams={{
                                    fit: 'fill',
                                    fill: 'solid',
                                    'fill-color': '00FFFFFF',
                                    ...IMGIX_SHARP_PARAMS,
                                  }}
                                  attributeConfig={{
                                    src: 'data-src',
                                    srcSet: 'data-srcset',
                                    sizes: 'data-sizes',
                                  }}
                                  htmlAttributes={{
                                    src: `${process.env.REACT_APP_IMG_SOURCE}/quest/${partnership.logoFileName}?w=180&h=90&fit=fill&${IMGIX_LQIP_PARAMS}`,
                                    alt: partnership.name,
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      {questData?.partnershipInfo?.map(
                        (partnershipInfo, index) => (
                          <div key={index}>
                            <div className=' mt-4 text-gold-light text-uppercase font-montserrat small-title-2 fw-bold small tracking-widest mb-2'>
                              {partnershipInfo.title}
                            </div>
                            <p className='text-slate-gray mb-0 lh-relaxed pre font-paragraph small-paragraph mt-0'>
                              {partnershipInfo.description}
                            </p>
                          </div>
                        ),
                      )}
                    </section>
                  )}
                  {displayFacultyMembers?.length > 0 && (
                    <section id='faculty' className='mb-64px scroll-target'>
                      <h2 className='display-5 font-heading mb-5 sub-title text-rich-navy fw-medium'>
                        Meet Your Faculty & Group Leaders{' '}
                      </h2>
                      <div className='row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-3 g-4 faculty-grid'>
                        {displayFacultyMembers.map((member, index) => (
                          <div key={index} className='col faculty-member-item'>
                            <div
                              role='button'
                              tabIndex={0}
                              className='h-100 border-0 hover-lift border-radius-12 p-3 prosperity-card cursor-pointer'
                              onClick={() => {
                                setFacultyModalMember(member)
                              }}
                              onKeyDown={(e) => {
                                if (e.key !== 'Enter' && e.key !== ' ') return
                                e.preventDefault()
                                setFacultyModalMember(member)
                              }}>
                              <Imgix
                                className='lazyload card-img-top rounded-2 mb-3 faculty-image'
                                src={member.image}
                                width={500}
                                height={500}
                                disableQualityByDPR
                                imgixParams={{
                                  fit: 'crop',
                                  ...IMGIX_SHARP_PARAMS,
                                }}
                                attributeConfig={{
                                  src: 'data-src',
                                  srcSet: 'data-srcset',
                                  sizes: 'data-sizes',
                                }}
                                htmlAttributes={{
                                  src: `${member.image}?w=500&h=500&fit=crop&${IMGIX_LQIP_PARAMS}`,
                                  alt: member.name,
                                }}
                              />
                              <div className='card-body text-start'>
                                <h3 className='font-heading sub-title-3 text-rich-navy mb-2'>
                                  {member.name}
                                </h3>
                                <p className='text-gold-light mb-2 fw-medium small font-paragraph small-paragraph'>
                                  {member.title}
                                </p>
                                <p className='text-slate-gray small lh-relaxed font-paragraph small-paragraph'>
                                  {member.expertise}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}
                  {(questData?.experientialInclusions?.length > 0 ||
                    questData?.digitalInclusions?.length > 0) && (
                    <section
                      ref={inclusionsRef}
                      id='program'
                      className='scroll-target'>
                      <h2 className='display-5 font-heading sub-title mb-5 text-rich-navy'>
                        Program Inclusions
                      </h2>
                      <div className='row g-4'>
                        <div className='col-md-6 pe-md-3'>
                          <div className='included-section bg-white border-radius-12 p-32px prosperity-card h-100'>
                            <div className='text-gold-light text-uppercase font-montserrat small-title fw-bold small tracking-widest mb-4'>
                              Experiential Learning
                            </div>
                            <ul className='list-unstyled vstack gap-3'>
                              {questData?.experientialInclusions?.map(
                                (item, index) => (
                                  <li
                                    key={index}
                                    className='included-item d-flex'>
                                    <span className='text-gray-700'>
                                      {item.inclusion}
                                    </span>
                                  </li>
                                ),
                              )}
                            </ul>
                          </div>
                        </div>
                        <div className='col-md-6 ps-md-3'>
                          <div className='included-section bg-white border-radius-12 p-32px prosperity-card h-100'>
                            <div className='text-gold-light text-uppercase font-montserrat small-title fw-bold small tracking-widest mb-4'>
                              Coaching, Education & Impact
                            </div>
                            <ul className='list-unstyled vstack gap-3'>
                              {questData?.digitalInclusions?.map(
                                (item, index) => (
                                  <li
                                    key={index}
                                    className='included-item d-flex'>
                                    <span className='text-gray-700'>
                                      {item.inclusion}
                                    </span>
                                  </li>
                                ),
                              )}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </section>
                  )}
                </div>

                <aside className='col-lg-4 ps-lg-4 pt-5 pt-lg-0 prosperity-booking-column'>
                  <div
                    ref={pricingBoxRef}
                    className='booking-card border border-gold-light border-radius-12 p-32px bg-white text-rich-navy shadow-lg sticky-top-custom'>
                    <div className='mb-40px'>
                      <div className='text-gold-light text-uppercase font-montserrat small-title-2 fw-bold small tracking-widest mb-3'>
                        Program
                      </div>
                      <div className='vstack gap-2'>
                        <p className='text-rich-navy fw-semibold mb-0'>
                          {questData?.programType}{' '}
                        </p>
                      </div>
                    </div>
                    <hr className='mb-40px' />
                    <div className='mb-40px'>
                      <div className='text-gold-light text-uppercase font-montserrat small-title-2 fw-bold small tracking-widest mb-3'>
                        Dates
                      </div>
                      <div className='vstack'>
                        {questData?.programDatesList?.length > 0 ? (
                          sortProgramDatesList(questData.programDatesList).map(
                            (dateItem, idx, sortedDates) => (
                              <p
                                key={idx}
                                className={`text-rich-navy fw-semibold text-capitalize ${
                                  idx < sortedDates.length - 1 ? 'mb-3' : 'mb-0'
                                }`}>
                                {formatProgramDateEntry({
                                  ...dateItem,
                                  description:
                                    dateItem.description?.trim() ||
                                    (questData?.location
                                      ? `${questData.location} Experience`
                                      : 'Experience'),
                                })}
                              </p>
                            ),
                          )
                        ) : (
                          <p className='text-rich-navy fw-semibold mb-0 text-capitalize'>
                            {(() => {
                              const { start, end } =
                                getProgramDatesRange(questData)
                              return getFormattedDateRange(start, end)
                            })()}
                          </p>
                        )}
                      </div>
                    </div>
                    <hr className='mb-40px' />
                    <div className='d-flex justify-content-between align-items-center'>
                      <div className='text-gold-light text-uppercase fw-bold small font-montserrat small-title-2 tracking-widest mb-0'>
                        Investment
                      </div>
                      <p className='text-rich-navy fs-4 fw-bold mb-0'>
                        US$
                        {questData?.investments?.standardRate
                          ? formatNumberWithCommas(
                              questData?.investments?.standardRate,
                            )
                          : 0}
                      </p>
                    </div>
                    <div className='mt-10px mb-40px d-flex justify-content-between align-items-center'>
                      <div className='text-rich-navy fw-semibold mb-0 text-capitalize'>
                        Single Occupancy
                      </div>
                      <p className='text-rich-navy fw-semibold mb-0'>
                        US$
                        {questData?.investments?.singleSupplement
                          ? formatNumberWithCommas(
                              questData?.investments?.singleSupplement,
                            )
                          : 0}
                      </p>
                    </div>
                    {/* {questData?.groupSize && (
                    <>
                      <hr className='mb-40px' />
                      <div className='mb-40px'>
                        <div className='text-gold-light text-uppercase font-montserrat small-title-2 fw-bold small tracking-widest mb-3'>
                          Experience size
                        </div>
                        <div className='vstack gap-2'>
                          <p className='text-rich-navy fw-semibold mb-0'>
                            Limited to {questData.groupSize}.
                          </p>
                          <p className='text-rich-navy mb-0'>
                            Submit application below to reserve your spot.
                          </p>
                        </div>
                      </div>
                    </>
                  )} */}
                    <hr className='mb-40px' />
                    <ScheduleCallButton className='btn btn-gold text-white w-100 font-montserrat py-3 small-title text-uppercase prosperity-button-cta button-transition'>
                      {questData?.inquiryButtonName || 'Apply Now'}{' '}
                    </ScheduleCallButton>
                    <button
                      type='button'
                      onClick={() => scrollToSection('learn-more')}
                      className='btn btn-link w-100 mt-3 p-0 text-decoration-underline text-rich-navy font-paragraph small-paragraph-3 prosperity-nav-link booking-card-question-link'>
                      Have a Question?
                    </button>
                  </div>
                </aside>
              </div>
              {questData?.mainVideo?.youtubeLink && (
                <section className='my-64px'>
                  <h2 className='display-5 font-heading sub-title mb-5 text-rich-navy fw-medium'>
                    {questData?.mainVideo?.title}
                  </h2>
                  <FancyboxVideoWrapper playIcon='aspect-ratio-21x9 w-100 rounded-3 overflow-hidden shadow-lg'>
                    <a
                      href={questData?.mainVideo?.youtubeLink}
                      data-fancybox='featured-video'
                      data-caption={questData?.mainVideo?.title}
                      className='w-100 h-100 position-relative d-block'>
                      <div className='w-100 h-100 parallax-container border-radius-12'>
                        {(() => {
                          const posterSrc = questData?.mainVideo
                            ?.posterImageFileName
                            ? `${process.env.REACT_APP_IMG_SOURCE}/quest/${questData.mainVideo.posterImageFileName}`
                            : 'https://abroad.imgix.net/abroad-website/yt_thumbnail.png'
                          const posterW = questData?.mainVideo
                            ?.posterImageFileName
                            ? 1920
                            : 1504
                          const posterH = questData?.mainVideo
                            ?.posterImageFileName
                            ? 822
                            : 846
                          return (
                            <Imgix
                              className='lazyload w-100 h-100 object-cover parallax-image'
                              src={posterSrc}
                              width={posterW}
                              height={posterH}
                              disableQualityByDPR
                              imgixParams={{
                                fit: 'crop',
                                ...IMGIX_SHARP_PARAMS,
                              }}
                              attributeConfig={{
                                src: 'data-src',
                                srcSet: 'data-srcset',
                                sizes: 'data-sizes',
                              }}
                              htmlAttributes={{
                                src: `${posterSrc}?w=${posterW}&h=${posterH}&fit=crop&${IMGIX_LQIP_PARAMS}`,
                                alt:
                                  questData?.mainVideo?.title ||
                                  'Video Thumbnail',
                              }}
                            />
                          )
                        })()}
                        <div className='position-absolute top-50 start-50 translate-middle'>
                          <div className='play-button-wrapper'>
                            <Play className='w-8 h-8 text-black fill-current' />
                          </div>
                        </div>
                      </div>
                    </a>
                  </FancyboxVideoWrapper>
                </section>
              )}
              {/* Testimonials Section - First Row */}
              {questData?.testimonialVideos?.firstRow &&
                Array.isArray(questData?.testimonialVideos?.firstRow.videos) &&
                questData?.testimonialVideos?.firstRow.videos.length > 0 && (
                  <section className='mb-64px'>
                    <TestimonialStrip
                      videos={questData?.testimonialVideos?.firstRow.videos
                        .filter(
                          (video) =>
                            video.youtubeLink &&
                            video.youtubeLink.includes('v='),
                        )
                        .map((video) => video.youtubeLink.split('v=')[1])}
                      title={
                        questData?.testimonialVideos?.firstRow.title ||
                        `Reflections from ${questData?.location}`
                      }
                      testimonialInfo={questData?.testimonialVideos?.firstRow.videos
                        .filter(
                          (video) =>
                            video.youtubeLink &&
                            video.youtubeLink.includes('v='),
                        )
                        .map((video) => video.title)}
                    />
                  </section>
                )}

              {displayJourneySteps?.length > 0 && (
                <section
                  id='journey'
                  className={`scroll-target ${
                    !hasContentBeforeJourney() ? 'mt-64px' : ''
                  }`}>
                  <h2 className='mb-5 text-rich-navy fw-medium font-heading sub-title'>
                    Your Journey
                  </h2>
                  <div className='vstack'>
                    {displayJourneySteps.map((step, index) => (
                      <JourneyStep key={index} step={step} index={index} />
                    ))}
                  </div>
                </section>
              )}

              {/* Testimonials Section - Second Row */}
              {questData?.testimonialVideos?.secondRow &&
                Array.isArray(questData?.testimonialVideos?.secondRow.videos) &&
                questData?.testimonialVideos?.secondRow.videos.length > 0 && (
                  <section className='mb-64px'>
                    <TestimonialStrip
                      videos={questData?.testimonialVideos?.secondRow.videos
                        .filter(
                          (video) =>
                            video.youtubeLink &&
                            video.youtubeLink.includes('v='),
                        )
                        .map((video) => video.youtubeLink.split('v=')[1])}
                      title={
                        questData?.testimonialVideos?.secondRow.title ||
                        'More Reflections'
                      }
                      testimonialInfo={questData?.testimonialVideos?.secondRow.videos
                        .filter(
                          (video) =>
                            video.youtubeLink &&
                            video.youtubeLink.includes('v='),
                        )
                        .map((video) => video.title)}
                    />
                  </section>
                )}

              {/* Testimonials Section - Third Row */}
              {questData?.testimonialVideos?.thirdRow &&
                Array.isArray(questData?.testimonialVideos?.thirdRow.videos) &&
                questData?.testimonialVideos?.thirdRow.videos.length > 0 && (
                  <section className='mb-64px'>
                    <TestimonialStrip
                      videos={questData?.testimonialVideos?.thirdRow.videos
                        .filter(
                          (video) =>
                            video.youtubeLink &&
                            video.youtubeLink.includes('v='),
                        )
                        .map((video) => video.youtubeLink.split('v=')[1])}
                      title={
                        questData?.testimonialVideos?.thirdRow.title ||
                        'More Reflections'
                      }
                      testimonialInfo={questData?.testimonialVideos?.thirdRow.videos
                        .filter(
                          (video) =>
                            video.youtubeLink &&
                            video.youtubeLink.includes('v='),
                        )
                        .map((video) => video.title)}
                    />
                  </section>
                )}
              {displayFaqs?.length > 0 && (
                <section id='faq' className='mb-5 scroll-target'>
                  <h2 className='display-5 font-heading sub-title mb-5 text-rich-navy fw-medium'>
                    Frequently Asked Questions
                  </h2>
                  <div className='bg-white rounded-3 p-32px prosperity-card'>
                    <div
                      className='accordion accordion-flush'
                      id='faqAccordionParent'>
                      {displayFaqs.map((faqItem, index) => (
                        <FAQ
                          key={index}
                          question={faqItem.question}
                          answer={faqItem.answer}
                        />
                      ))}
                    </div>
                  </div>
                </section>
              )}

              <LearnMoreSection
                questId={questId}
                informationSession={informationSession}
              />
            </div>
          </main>

          <VideoModal
            videoId={modalVideoId}
            show={!!modalVideoId}
            onClose={closeModal}
          />

          <FacultyModal
            show={!!facultyModalMember}
            faculty={facultyModalMember}
            onHide={() => setFacultyModalMember(null)}
          />

          <div
            className={`offcanvas offcanvas-end bg-white prosperity-mobile-menu ${
              isMenuOpen ? 'show' : ''
            }`}
            tabIndex='-1'
            id='mobileMenu'
            aria-labelledby='mobileMenuLabel'
            style={{ visibility: isMenuOpen ? 'visible' : 'hidden' }}>
            <div className='offcanvas-header'>
              <a href='https://abroad.io' className='navbar-brand'>
                <img
                  src='https://images.squarespace-cdn.com/content/v1/63db562b53fc5e6e59ef802c/98522d73-daa5-49ab-bc3c-0468ca75ee95/Group+1989.png'
                  alt='Abroad Logo'
                  style={{ height: '48px', width: '80px' }}
                />
              </a>

              <div
                className='cursor-pointer'
                onClick={() => setIsMenuOpen(false)}>
                <span className='icon icon-cross font-24' />
              </div>
            </div>
            <div className='offcanvas-body d-flex flex-column align-items-center pt-5'>
              <nav className='nav flex-column text-center gap-4'>
                <button
                  onClick={() => scrollToSection('program')}
                  className='btn btn-link text-rich-navy text-decoration-none fs-4 prosperity-nav-link'>
                  Program
                </button>
                <button
                  onClick={() => scrollToSection('journey')}
                  className='btn btn-link text-rich-navy text-decoration-none fs-4 prosperity-nav-link'>
                  Journey
                </button>
                <button
                  onClick={() => scrollToSection('faculty')}
                  className='btn btn-link text-rich-navy text-decoration-none fs-4 prosperity-nav-link'>
                  Faculty
                </button>
                <button
                  onClick={() => scrollToSection('faq')}
                  className='btn btn-link text-rich-navy text-decoration-none fs-4 prosperity-nav-link'>
                  FAQ
                </button>
                <button
                  onClick={() => scrollToSection('learn-more')}
                  className='btn btn-link text-rich-navy text-decoration-none fs-4 prosperity-nav-link'>
                  Learn More
                </button>
              </nav>
            </div>
          </div>
          {isMenuOpen && (
            <div
              className='offcanvas-backdrop fade show'
              onClick={() => setIsMenuOpen(false)}></div>
          )}
        </div>
        <ScheduleCallModal
          questId={questId}
          inquiryQuestion={questData?.inquiryQuestions}
          questInquiryCustomFormId={questData?.questInquiryCustomFormId}
          investments={questData?.investments}
        />
      </ModalProvider>
    </>
  )
}

export default Prosperity
